import apiClient from './client'

import type {
  PaginatedResponse,
  TransporteReserva,
  TransporteReservaInput,
  TransporteReservaListItem,
  TransporteVehiculo,
  TransporteVehiculoInput,
  TransporteVehiculoListItem,
  TransporteViaje,
  TransporteRevision,
} from '@/types/gia'

export interface TransporteVehiculosFilters {
  page?: number
  page_size?: number
  q?: string
  centro?: number
  estado?: string
}

export interface TransporteReservasFilters {
  page?: number
  page_size?: number
  q?: string
  centro?: number
  vehiculo?: number
  estado?: string
  desde?: string
  hasta?: string
}

export interface TransporteRecurringRuleInput {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly'
  interval?: number
  weekdays?: number[]
  count?: number
  until?: string
  no_end?: boolean
  horizon_days?: number
  conflict_strategy?: 'skip' | 'error'
}

const transporteApi = {
  listVehiculos: async (filters: TransporteVehiculosFilters = {}): Promise<PaginatedResponse<TransporteVehiculoListItem>> => {
    const response = await apiClient.get<PaginatedResponse<TransporteVehiculoListItem>>('/transporte/vehiculos/', {
      params: filters,
    })

    
return response.data
  },

  getVehiculo: async (id: number): Promise<TransporteVehiculo> => {
    const response = await apiClient.get<TransporteVehiculo>(`/transporte/vehiculos/${id}/`)

    
return response.data
  },

  createVehiculo: async (data: TransporteVehiculoInput): Promise<TransporteVehiculo> => {
    const response = await apiClient.post<TransporteVehiculo>('/transporte/vehiculos/', data)

    
return response.data
  },

  updateVehiculo: async (id: number, data: Partial<TransporteVehiculoInput>): Promise<TransporteVehiculo> => {
    const response = await apiClient.patch<TransporteVehiculo>(`/transporte/vehiculos/${id}/`, data)

    
return response.data
  },

  deleteVehiculo: async (id: number): Promise<void> => {
    await apiClient.delete(`/transporte/vehiculos/${id}/`)
  },

  addRevision: async (vehiculoId: number, data: Omit<TransporteRevision, 'id' | 'vehiculo' | 'tipo_display' | 'created_at'> & { tipo: string }): Promise<TransporteRevision> => {
    const response = await apiClient.post<TransporteRevision>(`/transporte/vehiculos/${vehiculoId}/add_revision/`, data)

    
return response.data
  },

  listReservas: async (filters: TransporteReservasFilters = {}): Promise<PaginatedResponse<TransporteReservaListItem>> => {
    const response = await apiClient.get<PaginatedResponse<TransporteReservaListItem>>('/transporte/reservas/', {
      params: filters,
    })

    
return response.data
  },

  getReserva: async (id: number): Promise<TransporteReserva> => {
    const response = await apiClient.get<TransporteReserva>(`/transporte/reservas/${id}/`)

    
return response.data
  },

  createReserva: async (data: TransporteReservaInput): Promise<TransporteReserva> => {
    const response = await apiClient.post<TransporteReserva>('/transporte/reservas/', data)

    
return response.data
  },

  createRecurringReserva: async (
    data: TransporteReservaInput & { recurrence: TransporteRecurringRuleInput }
  ): Promise<{
    created_count: number
    skipped_conflicts: number
    created_ids: number[]
    auto_limited_until?: string
  }> => {
    const response = await apiClient.post('/transporte/reservas/create_recurring/', data)

    
return response.data
  },

  updateReserva: async (id: number, data: Partial<TransporteReservaInput>): Promise<TransporteReserva> => {
    const response = await apiClient.patch<TransporteReserva>(`/transporte/reservas/${id}/`, data)

    
return response.data
  },

  deleteReserva: async (id: number): Promise<void> => {
    await apiClient.delete(`/transporte/reservas/${id}/`)
  },

  availableVehiculos: async (params: { inicio: string; fin: string; centro?: number }): Promise<TransporteVehiculoListItem[]> => {
    const response = await apiClient.get<TransporteVehiculoListItem[]>('/transporte/reservas/available/', {
      params,
    })

    
return response.data
  },

  cancelarReserva: async (id: number): Promise<TransporteReserva> => {
    const response = await apiClient.post<TransporteReserva>(`/transporte/reservas/${id}/cancelar/`)

    
return response.data
  },

  iniciarViaje: async (id: number): Promise<TransporteReserva> => {
    const response = await apiClient.post<TransporteReserva>(`/transporte/reservas/${id}/iniciar_viaje/`)

    
return response.data
  },

  cerrarViaje: async (id: number, payload: { kilometros_fin: number; observaciones?: string }): Promise<TransporteReserva> => {
    const response = await apiClient.post<TransporteReserva>(`/transporte/reservas/${id}/cerrar_viaje/`, payload)

    
return response.data
  },

  listViajes: async (filters: { page?: number; page_size?: number; centro?: number; vehiculo?: number; estado?: string } = {}): Promise<PaginatedResponse<TransporteViaje>> => {
    const response = await apiClient.get<PaginatedResponse<TransporteViaje>>('/transporte/viajes/', {
      params: filters,
    })

    
return response.data
  },
}

export default transporteApi
